import axios, { AxiosError } from 'axios'
import camelcaseKeys from 'camelcase-keys'
import { useApiStatusStore } from '@/stores/api-status'

export const API_URL = import.meta.env.VITE_API_URL
export const ERROR_NETWORK = 'Something went wrong. Please try again.'
export const SERVER_ERROR = 'Something went wrong and has been reported.'
export const SERVER_VALIDATION_ERROR = "That didn't work. Please check and try again."
export const enum REQUEST_METHODS {
  GET = 'get',
  POST = 'post',
  PATCH = 'patch',
  DELETE = 'delete'
}
export const enum STATUS_CODES {
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  NOT_FOUND = 404,
  UNPROCESSABLE_ENTITY = 422,
  INTERNAL_SERVER = 500,
  NETWORK_ERROR = 0
}
export enum FILTER_OPERANDS {
  EQUAL = '==',
  INCLUDES = 'ilike',
  LESS_THAN = '<',
  GREATHER_THAN = '>',
  LESS_THAN_EQUAL = '<=',
  GREATHER_THAN_EQUAL = '>=',
  IN = 'in',
  NOT_IN = 'not_in'
}

export function pathWithPageAndFilters(
  base: string,
  page: ApiPage,
  filters: ApiFilter[] = []
): string {
  if (page && filters.length > 0) {
    const filtersList = buildFilters(filters)
    return `${base}?page=${page}&${filtersList}`
  } else if (page) {
    return `${base}?page=${page}`
  } else if (filters) {
    return `${base}?${filters}`
  } else {
    return base
  }
}

/**
 * @Deprecated
 * Part of the migration to new responses
 * Use doRequest instead
 **/
export const doRequestOld = async (
  method: string,
  endpoint: string,
  data: any = {}
): Promise<any> => {
  try {
    const result = await axios.request({
      method,
      url: `${API_URL}${endpoint}`,
      data: data,
      withCredentials: true
    })

    return result.data
  } catch (error: any) {
    if (isNetworkOrTimeoutError(error.code)) {
      const apiStatusStore = useApiStatusStore()
      apiStatusStore.setApiAvailability(false)
      return { error: ERROR_NETWORK, errorCode: error.code }
    } else if (error.response?.status) {
      const base = {
        status_code: error.response.status,
        server_error: error.response.data?.error,
        errorCode: error.code,
        ...(error.response.data || {})
      }

      if (error.response.status >= STATUS_CODES.INTERNAL_SERVER) {
        return { ...base, error: ERROR_NETWORK }
      } else if (error.response.status == STATUS_CODES.UNPROCESSABLE_ENTITY) {
        return { ...base, error: SERVER_VALIDATION_ERROR }
      } else if (error.response.status >= STATUS_CODES.BAD_REQUEST) {
        return { ...base, error: SERVER_ERROR }
      } else {
        return { ...base, error: 'Something went wrong' }
      }
    } else {
      return { error: ERROR_NETWORK, errorCode: error.code }
    }
  }
}

export const doRequest = async <T = any>(
  method: string,
  endpoint: string,
  data: any = {}
): Promise<T> => {
  let response: any

  try {
    response = await axios.request({
      method,
      url: `${API_URL}${endpoint}`,
      data: data,
      withCredentials: true
    })
  } catch (error: any) {
    let errorToThrown = {
      errors: [{ code: 'network_error', message: ERROR_NETWORK }],
      statusCode: STATUS_CODES.INTERNAL_SERVER
    }

    if (isNetworkOrTimeoutError(error.code)) {
      const apiStatusStore = useApiStatusStore()
      apiStatusStore.setApiAvailability(false)

      errorToThrown = {
        errors: [{ code: 'network_error', message: ERROR_NETWORK }],
        statusCode: STATUS_CODES.NETWORK_ERROR
      }
    } else if (error.response?.status) {
      if (error.response.status >= STATUS_CODES.INTERNAL_SERVER) {
        errorToThrown = {
          statusCode: error.response.status,
          errors: [{ code: 'internal_server_error', message: SERVER_ERROR }]
        }
      } else {
        errorToThrown = {
          statusCode: error.response.status,
          errors: error.response?.data?.errors ?? [{ code: 'unknown_error', message: SERVER_ERROR }]
        }
      }
    }

    throw errorToThrown
  }

  if (response.data.success) {
    return camelcaseKeys(response.data.data, { deep: true }) as T
  } else {
    throw {
      errors: response.data.errors,
      statusCode: response.data.status_code
    }
  }
}

const buildFilter = (filter: ApiFilter, index: number) => {
  let values = `filters[${index}][value]=${filter.value}`

  if (filter.op == FILTER_OPERANDS.IN || filter.op == FILTER_OPERANDS.NOT_IN) {
    values = (filter.value as string[])
      .map((value) => `filters[${index}][value][]=${value}`)
      .join('&')
  }

  return `filters[${index}][field]=${filter.field}&filters[${index}][op]=${filter.op}&${values}`
}

export const buildFilters = (filters: ApiFilter[] = []) => {
  const filtersToStrings = filters.map((filter, index) => buildFilter(filter, index))
  return filtersToStrings.join('&')
}

/**
 * @Deprecated
 * Part of the migration to new responses
 * Use doPost instead
 **/
export const doPostOld = async (endpoint: string, data: any = {}): Promise<any> => {
  return doRequestOld(REQUEST_METHODS.POST, endpoint, data)
}

/**
 * @Deprecated
 * Part of the migration to new responses
 * Use doGet instead
 **/
export const doGetOld = async (endpoint: string): Promise<any> => {
  return doRequestOld(REQUEST_METHODS.GET, endpoint)
}

/**
 * @Deprecated
 * Part of the migration to new responses
 * Use doPatch instead
 **/
export const doPatchOld = async (endpoint: string, data: any = {}): Promise<any> => {
  return doRequestOld(REQUEST_METHODS.PATCH, endpoint, data)
}

/**
 * @Deprecated
 * Part of the migration to new responses
 * Use doDelete instead
 **/
export const doDeleteOld = async (endpoint: string): Promise<any> => {
  return doRequestOld(REQUEST_METHODS.DELETE, endpoint)
}

export const doGet = async (endpoint: string): Promise<any> => {
  return doRequest(REQUEST_METHODS.GET, endpoint)
}

export const doPost = async (endpoint: string, data: any = {}): Promise<any> => {
  return doRequest(REQUEST_METHODS.POST, endpoint, data)
}

export const doPatch = async (endpoint: string, data: any = {}): Promise<any> => {
  return doRequest(REQUEST_METHODS.PATCH, endpoint, data)
}

export const doDelete = async (endpoint: string): Promise<any> => {
  return doRequest(REQUEST_METHODS.DELETE, endpoint)
}

export const isNetworkOrTimeoutError = (errorCode: any): boolean => {
  return (
    errorCode === AxiosError.ERR_NETWORK ||
    errorCode === AxiosError.ECONNABORTED ||
    errorCode === AxiosError.ETIMEDOUT
  )
}

export const centsToDollars = (cents: number): number => cents / 100

export default {
  doGetOld,
  doPostOld,
  doPatchOld,
  doDeleteOld,
  doGet,
  doPost,
  doPatch,
  doDelete,
  isNetworkOrTimeoutError
}
